import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { AppHeader } from "@/components/AppHeader";
import { UserPlus, Key, Trash, X, CheckCircle, XCircle } from "@phosphor-icons/react";

const LABEL = "mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground";
const INPUT = "w-full border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary transition-colors duration-200";

export default function TeamPage() {
  const { t } = useLang();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [pwdFor, setPwdFor] = useState(null); // member id currently resetting pwd
  const [pwdVal, setPwdVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "sales_rep", password: "" });

  const load = () => { setLoading(true); axios.get(`${API}/admin/team`).then((r) => setMembers(r.data)).catch(() => toast.error(t.saveFailed)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const addMember = async () => {
    if (!form.email.trim()) { toast.error(t.email + " *"); return; }
    if (form.password.length < 6) { toast.error(t.passwordHint); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/team`, form);
      toast.success(t.memberAdded);
      setShowAdd(false); setForm({ name: "", email: "", role: "sales_rep", password: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); } finally { setBusy(false); }
  };

  const patch = async (id, body, okMsg) => {
    setBusy(true);
    try { await axios.put(`${API}/admin/team/${id}`, body); toast.success(okMsg || t.memberUpdated); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); }
    finally { setBusy(false); }
  };

  const resetPwd = async (id) => {
    if (pwdVal.length < 6) { toast.error(t.passwordHint); return; }
    setBusy(true);
    try { await axios.post(`${API}/admin/team/${id}/password`, { password: pwdVal }); toast.success(t.passwordUpdated); setPwdFor(null); setPwdVal(""); }
    catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); }
    finally { setBusy(false); }
  };

  const removeMember = async (id) => {
    if (!window.confirm(t.confirmDeleteMember)) return;
    setBusy(true);
    try { await axios.delete(`${API}/admin/team/${id}`); toast.success(t.memberDeleted); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t.saveFailed); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-4xl px-4 sm:px-6 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4 border-b border-border pb-5">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">{t.team}</div>
            <h1 className="mt-1 font-display text-2xl font-black tracking-tighter text-foreground">{t.teamMembers}</h1>
          </div>
          <button data-testid="add-member-button" onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 bg-primary px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5">
            <UserPlus size={16} weight="bold" /> {t.addMember}
          </button>
        </div>

        {loading ? (
          <div className="py-16 text-center text-muted-foreground">{t.loading}</div>
        ) : (
          <div className="border border-border bg-card divide-y divide-border" data-testid="team-list">
            {members.map((m) => (
              <div key={m.id} data-testid={`member-${m.id}`} className="flex flex-wrap items-center gap-3 p-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-semibold text-foreground">{m.name || m.email}</span>
                    {m.id === user?.id && <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">({t.you})</span>}
                    {m.active ? <CheckCircle size={14} weight="fill" className="text-emerald-600" /> : <XCircle size={14} weight="fill" className="text-muted-foreground" />}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">{m.email}</div>
                </div>

                <select data-testid={`member-role-${m.id}`} value={m.role} disabled={busy}
                  onChange={(e) => patch(m.id, { role: e.target.value })} className="w-40 border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary transition-colors duration-200">
                  <option value="admin">{t.roleAdmin}</option>
                  <option value="sales_rep">{t.roleSalesRep}</option>
                </select>

                <button data-testid={`member-toggle-${m.id}`} onClick={() => patch(m.id, { active: !m.active }, t.memberUpdated)} disabled={busy}
                  className="border border-border px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-foreground hover:bg-muted transition-colors duration-200 disabled:opacity-50">
                  {m.active ? t.deactivate : t.activate}
                </button>
                <button data-testid={`member-pwd-${m.id}`} onClick={() => { setPwdFor(pwdFor === m.id ? null : m.id); setPwdVal(""); }} disabled={busy}
                  className="inline-flex items-center gap-1 border border-border px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-foreground hover:bg-muted transition-colors duration-200 disabled:opacity-50">
                  <Key size={13} weight="bold" /> {t.resetPassword}
                </button>
                <button data-testid={`member-delete-${m.id}`} onClick={() => removeMember(m.id)} disabled={busy}
                  className="text-muted-foreground hover:text-primary transition-colors duration-200 disabled:opacity-50"><Trash size={16} /></button>

                {pwdFor === m.id && (
                  <div className="flex w-full items-center gap-2 pt-2" data-testid={`member-pwd-row-${m.id}`}>
                    <input type="text" value={pwdVal} onChange={(e) => setPwdVal(e.target.value)} placeholder={t.newPassword}
                      className={`${INPUT} max-w-xs`} data-testid={`member-pwd-input-${m.id}`} />
                    <button onClick={() => resetPwd(m.id)} disabled={busy} data-testid={`member-pwd-save-${m.id}`}
                      className="bg-primary px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-white disabled:opacity-50">{t.save}</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" data-testid="add-member-modal">
          <div className="mt-16 w-full max-w-md border border-border bg-card p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-xl font-black tracking-tight text-foreground">{t.newMember}</h2>
              <button data-testid="close-add-member" onClick={() => setShowAdd(false)} className="text-muted-foreground hover:text-foreground"><X size={20} weight="bold" /></button>
            </div>
            <div className="space-y-3">
              <label className="block"><span className={LABEL}>{t.name}</span><input data-testid="am-name" className={INPUT} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
              <label className="block"><span className={LABEL}>{t.email} *</span><input data-testid="am-email" className={INPUT} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
              <label className="block"><span className={LABEL}>{t.role}</span>
                <select data-testid="am-role" className={INPUT} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  <option value="sales_rep">{t.roleSalesRep}</option>
                  <option value="admin">{t.roleAdmin}</option>
                </select></label>
              <p className="text-[11px] text-muted-foreground">{t.salesRepLimits}</p>
              <label className="block"><span className={LABEL}>{t.initialPassword} *</span><input data-testid="am-password" type="text" className={INPUT} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></label>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button data-testid="am-cancel" onClick={() => setShowAdd(false)} className="border border-border px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-foreground hover:bg-muted transition-colors duration-200">{t.cancel}</button>
              <button data-testid="am-submit" onClick={addMember} disabled={busy} className="bg-primary px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">{t.addMember}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
